import {inject} from '@angular/core';
import {
  EMPTY,
  catchError,
  concatMap,
  expand,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  timer,
  withLatestFrom
} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from "@ngrx/store";
import {ServicesHttpService} from "../services/services-http.service";
import {ServicesActions, ServicesAPiActions, ServicesChannelActions} from "./services.action";
import {Service} from "../../models/service.model";
import {ServicesChanelService} from "../services/services-chanel.service";
import {servicesFeature} from "./services.state";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";

export const getService = createEffect(
  (actions$ = inject(Actions),
   servicesHttpService = inject(ServicesHttpService),
   commonsFacadeService = inject(CommonsFacadeService),
   servicesStore = inject(Store<Service>),
  ) => {
    return actions$.pipe(
      ofType(ServicesAPiActions.getService),
      mergeMap(payload => {
        return servicesHttpService.getService(payload.serviceId).pipe(
          map((service: Service) => ServicesAPiActions.getServiceSuccess({service: service, isSubscribeToChannel: payload.isSubscribeToChannel})),
          catchError((error: {
            message: string
          }) => of(ServicesAPiActions.getServiceFailure({error})))
        )
      })
    );
  },
  {functional: true}
);

export const getInfiniteScrollServices = createEffect(
  (actions$ = inject(Actions),
   servicesHttpService = inject(ServicesHttpService),
   servicesStore = inject(Store<Service>),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(ServicesAPiActions.getInfiniteScrollServices),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(servicesStore.select(servicesFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return servicesHttpService.getServices(paramsCopy).pipe(
          map((res: {
            list: Service[]
            cursor: string
          }) => ServicesAPiActions.getInfiniteScrollServicesSuccess({services: res.list, cursor: res.cursor, isSubscribeToChannel: payload.isSubscribeToChannel})),
          catchError((error: {
            message: string
          }) => of(ServicesAPiActions.getInfiniteScrollServicesFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getServices = createEffect(
  (actions$ = inject(Actions),
   servicesHttpService = inject(ServicesHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(ServicesAPiActions.getServices),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return servicesHttpService.getServices(payload.params).pipe(
          map((res: {
            list: Service[]
            cursor: string
          }) => ServicesAPiActions.getServicesSuccess({services: res.list, isSubscribeToChannel: payload.isSubscribeToChannel})),
          catchError((error: {
            message: string
          }) => of(ServicesAPiActions.getServicesFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getMapServicesInBatches = createEffect(
  (actions$ = inject(Actions),
   servicesHttpService = inject(ServicesHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(ServicesAPiActions.getMapServicesInBatches),
      switchMap((payload) => {
        const batchSize = payload.batchSize || 100;
        const maxItems = payload.maxItems || 1000;
        const intervalMs = payload.intervalMs || 1000;

        commonsFacadeService.setLoaderOn();

        return of({
          cursor: undefined as string | undefined,
          loaded: 0,
          shouldContinue: true,
          isFirstBatch: true
        }).pipe(
          expand((state) => {
            if (!state.shouldContinue) {
              return EMPTY;
            }

            return timer(state.isFirstBatch ? 0 : intervalMs).pipe(
              switchMap(() => {
                const paramsCopy = {
                  ...payload.params,
                  limit: batchSize
                };

                if (state.cursor) {
                  paramsCopy.cursor = state.cursor;
                }

                return servicesHttpService.getServices(paramsCopy).pipe(
                  map((res: { list: Service[], cursor?: string }) => {
                    const loaded = state.loaded + res.list.length;
                    return {
                      services: res.list,
                      cursor: res.cursor,
                      loaded,
                      shouldContinue: !!res.cursor && loaded < maxItems,
                      isFirstBatch: false
                    };
                  })
                );
              })
            );
          }),
          concatMap((state) => {
            if (!state.services?.length) {
              return EMPTY;
            }

            return of(ServicesActions.setServices({
              services: state.services
            }));
          }),
          catchError((error: { message: string }) => of(ServicesAPiActions.getMapServicesInBatchesFailure({error}))),
          takeUntil(actions$.pipe(ofType(ServicesActions.clearStore))),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        )
      })
    );
  },
  {functional: true}
);

export const getServiceSuccess = createEffect(
  (actions$ = inject(Actions),
   servicesChannelService = inject(ServicesChanelService),
   servicesStore = inject(Store<Service>)) => {
    return actions$.pipe(
      ofType(ServicesAPiActions.getServiceSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          servicesChannelService.subscribeById(payload.service.id);
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const getServicesSuccess = createEffect(
  (actions$ = inject(Actions),
   servicesChannelService = inject(ServicesChanelService),
   servicesStore = inject(Store<Service>)) => {
    return actions$.pipe(
      ofType(ServicesAPiActions.getServicesSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const serviceIds = payload.services.map(l => l.id);
          serviceIds.forEach(serviceId => servicesChannelService.subscribeById(serviceId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);


export const getInfiniteScrollServicesSuccess = createEffect(
  (actions$ = inject(Actions),
   servicesChannelService = inject(ServicesChanelService),
   servicesStore = inject(Store<Service>)) => {
    return actions$.pipe(
      ofType(ServicesAPiActions.getInfiniteScrollServicesSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const serviceIds = payload.services.map(l => l.id);
          serviceIds.forEach(serviceId => servicesChannelService.subscribeById(serviceId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const subscribeSetServicesChannels = createEffect(
  (actions$ = inject(Actions),
   servicesChannelService = inject(ServicesChanelService),
   servicesStore = inject(Store<Service>)) => {
    return actions$.pipe(
      ofType(ServicesActions.setServices),
      map((payload) => {
        const serviceIds = payload.services.map(l => l.id);
        serviceIds.forEach(serviceId => servicesChannelService.subscribeById(serviceId));
      })
    );
  },
  {functional: true, dispatch: false}
);

export const subscribeAllServicesChannel = createEffect(
  (actions$ = inject(Actions),
   servicesChanelService = inject(ServicesChanelService)) => {
    return actions$.pipe(
      ofType(ServicesChannelActions.subscribe),
      mergeMap(payload =>
        servicesChanelService.getChannelsValues().pipe(
          map((notification) => {
            return ServicesChannelActions.notificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const subscribeAllPartialServicesChannel = createEffect(
  (actions$ = inject(Actions),
   servicesChanelService = inject(ServicesChanelService)) => {
    return actions$.pipe(
      ofType(ServicesChannelActions.subscribePartial),
      mergeMap(payload =>
        servicesChanelService.getPartialChannelsValues().pipe(
          map((notification) => {
            return ServicesChannelActions.partialNotificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const unsubscribeAllServiceChannels = createEffect(
  (actions$ = inject(Actions),
   servicesChannelService = inject(ServicesChanelService)) => {
    return actions$.pipe(
      ofType(ServicesChannelActions.unsubscribe, ServicesActions.removeAll, ServicesActions.clearStore),
      map(payload => {
          servicesChannelService.unSubscribeAll();
          return ServicesChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);


export const unsubscribeServiceChannels = createEffect(
  (actions$ = inject(Actions),
   servicesChannelService = inject(ServicesChanelService)) => {
    return actions$.pipe(
      ofType(ServicesActions.removeServices),
      map(payload => {
          payload.ids?.forEach(serviceId => servicesChannelService.unSubscribeById(serviceId));
          return ServicesChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);


